import { ApiError } from '../../api/problem'
import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

/** A generation job, exactly as the API models it. Generated — nothing hand-written here. */
export type GenerationJob = Schema['GenerationJobDto']
export type JobStatus = Schema['GenerationJobStatus']
export type JobKind = Schema['GenerationJobKind']

const TERMINAL: JobStatus[] = ['Succeeded', 'Failed', 'Canceled']

/** Whether a status is one the job will not move off. */
export function isTerminal(status: JobStatus | undefined): boolean {
  return status !== undefined && TERMINAL.includes(status)
}

/**
 * How a failed job lookup should be read. **The single most important rule in this stage.**
 *
 * `GET /jobs/{id}` and `GET /jobs/{id}/events` answer `404` for two situations the client
 * cannot tell apart and must not try to: a job that never existed, and one that finished
 * more than the store's retention period ago and was swept. The registry is in-memory and
 * swept after thirty minutes.
 *
 * Reading that as an error would be wrong in the common case and wrong in the way that
 * matters: **a successful job persists its output before it reports success**, so by the
 * time the record is gone the picture is already saved and linked. The right response to a
 * missing job is therefore "assume it finished — refetch the owning sheet", which is what
 * `finished` means below.
 *
 * A `403` is different and stays an error: a job id is a capability, and being handed
 * somebody else's is a bug rather than a completion.
 */
export type JobLookupOutcome = 'finished' | 'error'

export function interpretJobLookupFailure(error: unknown): JobLookupOutcome {
  if (error instanceof ApiError && error.kind === 'not-found') return 'finished'
  return 'error'
}

/** What a job is generating for, as a sentence. */
const KIND_LABELS: Record<string, string> = {
  CharacterPortrait: 'Portrait',
  CharacterDescription: 'Description',
  LocationVisualization: 'Location image',
  LocationBattleMap: 'Battle map',
  ScenePageImage: 'Page image',
  ComicPageImage: 'Comic page',
}

export const jobKindLabel = (value: string): string => KIND_LABELS[value] ?? value

/**
 * The line a progress card shows.
 *
 * `assumedFinished` is the swept case: there is no job left to report on, and saying
 * "finished" is both the truth and the only useful thing to say.
 */
export function jobStatusLine(job: GenerationJob | null, assumedFinished: boolean): string {
  if (assumedFinished) return 'Finished — reloading.'
  if (job === null) return 'Starting…'

  switch (job.status) {
    case 'Queued':
      return 'Queued.'
    case 'Running':
      return 'Generating…'
    case 'Succeeded':
      return 'Done.'
    case 'Failed':
      return job.error ?? 'The generation failed.'
    case 'Canceled':
      return 'Canceled.'
    default:
      return 'Working…'
  }
}
